import { Activity } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useMaxwellRunDetail, useMaxwellRuns } from "@/hooks/useMaxwellRuns";
import { useInfiniteScroll } from "@/lib/use-infinite-scroll";

import { RunListItem } from "./activity/RunListItem";
import { RunStepTimeline } from "./activity/RunStepTimeline";

/**
 * The Activity section of the Maxwell workspace: the caller's autonomy runs as
 * a cursor-paged list (infinite scroll), with a per-run step timeline beside
 * it. Reuses the run-detail endpoint for the selected run.
 *
 * Selection is mirrored to the `?run=` query param so a `northstar://agent_run/
 * <id>` resource link (chat "View run", the inbox follow arrow) lands here
 * with that run already open, and the URL stays shareable/bookmarkable.
 */
export function MaxwellActivity() {
  const {
    data,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useMaxwellRuns();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlRunId = searchParams.get("run");
  // Only tracks an in-page pick; the `?run=` param (kept in sync by
  // `selectRun` below) is the source of truth whenever it's present, so a
  // deep link landing here — or the URL changing under this mounted page,
  // e.g. via browser back/forward — takes effect without an extra render
  // pass.
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const sentinelRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const runs = data?.pages.flatMap((p) => p.items) ?? [];
  // URL param wins (deep link); else the user's last in-page pick; else
  // default the detail pane to the newest run.
  const activeRunId = urlRunId ?? selectedRunId ?? runs[0]?.id ?? null;
  const detail = useMaxwellRunDetail(activeRunId);

  // Reflect a user pick into the URL (shareable deep-link) without pushing a
  // new history entry per click.
  const selectRun = (id: string) => {
    setSelectedRunId(id);
    setSearchParams({ run: id }, { replace: true });
  };

  // A deleted run can no longer be the selection — drop back to the URL-less
  // default (newest run) rather than leave `?run=` pointing at a 404.
  const clearSelection = () => {
    setSelectedRunId(null);
    setSearchParams({}, { replace: true });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <EmptyState
        icon={Activity}
        title="No runs yet"
        description="Give Maxwell a goal — from chat or a watch — and the run's progress shows up here, step by step."
      />
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[22rem_1fr] lg:items-start">
      <div className="flex flex-col overflow-hidden rounded-md border border-border">
        <div className="flex items-center justify-between border-b border-border px-4 py-2">
          <p className="text-xs font-medium text-muted-foreground">Runs</p>
          <p className="text-xs text-muted-foreground">
            {runs.length}
            {hasNextPage ? "+" : ""}
          </p>
        </div>
        <div className="divide-y divide-border">
          {runs.map((run) => (
            <RunListItem
              key={run.id}
              run={run}
              selected={run.id === activeRunId}
              onSelect={selectRun}
              onDeleted={() => {
                if (run.id === activeRunId) clearSelection();
              }}
            />
          ))}
        </div>
        <div ref={sentinelRef} />
        {isFetchingNextPage && (
          <div className="p-3">
            <Skeleton className="h-12 w-full" />
          </div>
        )}
      </div>

      <div className="rounded-md border border-border">
        <RunStepTimeline
          detail={detail.data}
          isLoading={detail.isLoading}
          onDeleted={clearSelection}
        />
      </div>
    </div>
  );
}
